"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React, { useEffect, useState } from "react";
import { Quote, Star } from "lucide-react";
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from "@/components/ui/carousel";
import { Card, CardContent } from "@/components/ui/card";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { cn } from "@/lib/utils";
const TESTIMONIALS = [{
  author: "Marcus T.",
  quote: "Cyril Consulting Group redefined our AI strategy, leading to a 40% increase in operational efficiency within six months. Their precision is unmatched.",
  position: "COO, Global Logistics Corp",
  avatar: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1577001/img/avatar_jane_d.png"
}, {
  author: "Elena S.",
  quote: "The cloud transformation roadmap provided was flawless. We migrated our entire healthcare infrastructure with zero downtime and absolute security.",
  position: "CTO, HealthScale Systems",
  avatar: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1577001/img/avatar_john_s.png"
}, {
  author: "David L.",
  quote: "Their technology advisory is unparalleled. They don't just recommend tools; they architect sustainable competitive advantages through intelligent automation.",
  position: "VP of Innovation, FinTech Global",
  avatar: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1577001/img/avatar_emily_r.png"
}, {
  author: "Sarah K.",
  quote: "Digital transformation was a black box for us until Cyril stepped in. Now, automated intelligence is our core growth engine for international markets.",
  position: "Director of Ops, Nexus Manufacturing",
  avatar: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1577001/img/avatar_michael_b.png"
}];
const LOGOS = [{
  name: "Financier Group",
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1577001/img/logo_techcorp.png"
}, {
  name: "BioHealth Solutions",
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1577001/img/logo_innovatex.png"
}, {
  name: "Logistics Prime",
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1577001/img/logo_globalsystems.png"
}, {
  name: "Future Banking",
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1577001/img/logo_futurenet.png"
}, {
  name: "Alpha AI Systems",
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1577001/img/logo_alphawave.png"
}, {
  name: "BlueSky Aviation",
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1577001/img/logo_bluesky.png"
}, {
  name: "Orbit Energy",
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1577001/img/logo_orbit.png"
}, {
  name: "Nexus Retail",
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1577001/img/logo_nexus.png"
}];
export default function ProvenTrackRecord() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll();
  const [api, setApi] = useState<any>();
  useEffect(() => {
    if (!api) return;
    const interval = setInterval(() => {
      api.scrollNext();
    }, 5000);
    return () => clearInterval(interval);
  }, [api]);
  return <section data-section-id="6295" id="trust-excellence" className="dark bg-background text-foreground py-24 relative overflow-hidden">
      {}
      <style jsx>{`
        @keyframes marquee {
          0% {
            transform: translateX(0);
          }
          100% {
            transform: translateX(-50%);
          }
        }
        .animate-marquee {
          animation: marquee 30s linear infinite;
        }
        .pause-on-hover:hover {
          animation-play-state: paused;
        }
      `}</style>

      <div className="container mx-auto px-4 relative z-10">
        {}
        <div ref={ref} className={cn("text-center mb-16 transition-all duration-700 transform", isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10")}>
          <h2 className="text-3xl md:text-5xl font-bold mb-4 tracking-tighter text-foreground uppercase">
            The Benchmark of Digital Authority
          </h2>
          <p className="text-muted-foreground max-w-2xl mx-auto text-lg font-sans leading-relaxed">
            Partnering with global enterprises to architect the future of intelligence, 
            cloud scale, and autonomous operations with surgical precision.
          </p>
        </div>

        {}
        <div className="w-full overflow-hidden mb-24 relative group">
          <div className="absolute left-0 top-0 bottom-0 w-20 bg-gradient-to-r from-background to-transparent z-10 pointer-events-none" />
          <div className="absolute right-0 top-0 bottom-0 w-20 bg-gradient-to-l from-background to-transparent z-10 pointer-events-none" />
          
          <div className="flex w-max animate-marquee pause-on-hover items-center">
            {}
            {LOGOS.map((logo, i) => <div key={`logo-1-${i}`} data-index={i} className="mx-8 md:mx-12 flex items-center justify-center opacity-40 hover:opacity-100 transition-opacity duration-500 grayscale hover:grayscale-0 cursor-pointer">
                <img src={logo.src} alt={logo.name} className="h-8 md:h-10 w-auto object-contain" />
              </div>)}
            {}
            {LOGOS.map((logo, i) => <div key={`logo-2-${i}`} data-index={i + LOGOS.length} className="mx-8 md:mx-12 flex items-center justify-center opacity-40 hover:opacity-100 transition-opacity duration-500 grayscale hover:grayscale-0 cursor-pointer">
                <img src={logo.src} alt={logo.name} className="h-8 md:h-10 w-auto object-contain" />
              </div>)}
             {}
             {LOGOS.map((logo, i) => <div key={`logo-3-${i}`} data-index={i + LOGOS.length * 2} className="mx-8 md:mx-12 flex items-center justify-center opacity-40 hover:opacity-100 transition-opacity duration-500 grayscale hover:grayscale-0 cursor-pointer">
                <img src={logo.src} alt={logo.name} className="h-8 md:h-10 w-auto object-contain" />
              </div>)}
          </div>
        </div>

        {}
        <div className="max-w-6xl mx-auto">
          <Carousel opts={{
          align: "start",
          loop: true
        }} setApi={setApi} className="w-full">
            <CarouselContent className="-ml-4">
              {TESTIMONIALS.map((testimonial, i) => <CarouselItem key={i} data-index={i} className="pl-4 md:basis-1/2 lg:basis-1/3">
                  <div className="h-full p-1">
                    <Card className="h-full bg-card/40 backdrop-blur-md border-border/40 hover:border-secondary/50 transition-all duration-500 relative overflow-hidden group shadow-lg hover:shadow-secondary/5">
                      <CardContent className="p-8 flex flex-col h-full relative z-10">
                        {}
                        <Quote className="absolute top-4 right-4 w-24 h-24 text-accent-foreground/10 -rotate-12 pointer-events-none transition-transform duration-700 group-hover:scale-110 group-hover:text-accent-foreground/20" />
                        
                        {}
                        <div className="flex gap-1 mb-6 text-secondary">
                          {[...Array(5)].map((_, starIndex) => <Star key={starIndex} data-index={starIndex} className="w-4 h-4 fill-current" />)}
                        </div>

                        {}
                        <blockquote className="flex-grow mb-8 relative">
                          <p className="text-lg text-foreground/90 leading-relaxed font-medium font-sans">
                            "{testimonial.quote}"
                          </p>
                        </blockquote>

                        {}
                        <div className="flex items-center gap-4 mt-auto pt-6 border-t border-border/30">
                          <SafeHydrate><Avatar className="h-12 w-12 border-2 border-secondary/30">
                            <AvatarImage src={testimonial.avatar} alt={testimonial.author} />
                            <AvatarFallback className="bg-accent/20 text-accent-foreground font-bold">
                              {testimonial.author.charAt(0)}
                            </AvatarFallback>
                          </Avatar></SafeHydrate>
                          <div>
                            <div className="font-semibold text-foreground tracking-tight">
                              {testimonial.author}
                            </div>
                            <div className="text-sm text-muted-foreground font-mono uppercase tracking-widest">
                              {testimonial.position}
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                </CarouselItem>)}
            </CarouselContent>
            <div className="hidden md:block">
              <CarouselPrevious className="left-[-3rem] bg-background/80 hover:bg-secondary hover:text-secondary-foreground border-border/40 transition-colors" />
              <CarouselNext className="right-[-3rem] bg-background/80 hover:bg-secondary hover:text-secondary-foreground border-border/40 transition-colors" />
            </div>
          </Carousel>
        </div>
      </div>
    </section>;
}